/**
 * CodeEntryBlock — Code redemption CTA card
 *
 * Renders a branded CTA that opens the existing CodeEntryModal
 * when clicked. Reuses the quest code redemption pipeline
 * (POST /event-pipeline/redeem-code).
 */

import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { FaKeyboard } from "react-icons/fa";
import { CodeEntryModal } from "@/components/challenge/CodeEntryModal";
import { QuestConfigMilestoneResponse } from "@devour/client";
import { toggleLoginOpen } from "@/redux/meta/metaActions";
import "./CodeEntryBlock.scss";

interface CodeEntryBlockProps {
    config?: {
        headline?: string;
        description?: string;
        buttonText?: string;
        questConfigId?: string;
        milestoneId?: string;
    };
}

export const CodeEntryBlock = ({ config }: CodeEntryBlockProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const dispatch = useDispatch();

    if (!config) return null;

    const headline = config.headline || "Enter Your Code";
    const description = config.description;
    const buttonText = config.buttonText || "ENTER CODE";
    const hasQuest = config.questConfigId && config.milestoneId;

    // CodeEntryModal only reads id/name/description — other fields unused for code entry flow
    const milestone = hasQuest ? {
        id: config.milestoneId,
        name: headline,
        description: description,
    } as unknown as QuestConfigMilestoneResponse : null;

    return (
        <div className="code-entry">
            <div className="code-entry__content">
                <FaKeyboard className="code-entry__icon" />
                <h3 className="code-entry__headline">{headline}</h3>
                {description &&
                    <p className="code-entry__description">{description}</p>
                }
                <button
                    className="code-entry__button"
                    onClick={() => fullToken ? setIsModalOpen(true) : dispatch(toggleLoginOpen(true))}
                    type="button"
                >
                    {buttonText}
                </button>
            </div>

            {milestone && config.questConfigId &&
                <CodeEntryModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    milestone={milestone}
                    questConfigId={config.questConfigId}
                />
            }
        </div>
    );
};

export default CodeEntryBlock;
